import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MaterialIcon from 'react/elements/MaterialIcon';
import { findIdeaNamed } from 'react/components/calendar/searchMealIdeas';
import { convertMealName } from 'redux/actions/mealIdeas';
import { selectMealIdeasIndex } from '../../../redux/food/selectors';
import useOffline, { onlineOnly } from '../../hooks/useOffline';

/**
 * For an item that was really a meal: "Just Egg and Sausage sandwiches" written on the list and put
 * on the calendar before meal ideas existed. Makes its name a meal idea, or links it to the idea
 * already called that, taking every calendar meal of the name and every bought row of the item along
 * (meal_idea/convert_meal_name).
 *
 * `details` are the item's, from grocery_item/show; `meals_named` is how many calendar meals carry its
 * name with no idea behind them. Once an idea of the name exists and no such meal is left, there is
 * nothing to do and nothing is shown.
 *
 * The idea can take the item as its ingredient, which fits a name bought as one thing and not one
 * that stood for a recipe, so it is left to the person and starts unticked. `onConverted` gets the
 * idea's id, and the details are reloaded through `onChanged`, since what the item is used in may
 * have changed. Online-only, so offline the button is disabled, titled "Needs a connection".
 */
export default function ItemAsMealIdea({ details, onConverted = null, onChanged = null }) {
	const dispatch = useDispatch();
	const ideasById = useSelector((state) => selectMealIdeasIndex(state).ideasById);
	const [withIngredient, setWithIngredient] = useState(false);
	const [saving, setSaving] = useState(false);
	const [outcome, setOutcome] = useState(null);
	const [error, setError] = useState(null);
	const offline = useOffline();
	const name = details.item.name;
	const idea = findIdeaNamed(Object.values(ideasById), name);
	const count = details.meals_named || 0;
	const usesItem = !!idea && (details.meal_ideas || []).some((used) => used.id === idea.id);

	if(outcome) {
		return (
			<section className='meal_idea_panel__section grocery_item_editor__as-idea'>
				<h6>Meal idea</h6>
				<p className='grocery_item_editor__muted'>{outcome}</p>
			</section>
		);
	}
	if(idea && count === 0) {
		return null;
	}

	const convert = () => {
		setSaving(true);
		setError(null);
		dispatch(convertMealName(null, name, { mealIdeaId: idea ? idea.id : null, withIngredient: withIngredient && !usesItem }))
			.then((result) => {
				setSaving(false);
				if(!result) {
					setError('That did not save. Try again.');
					return;
				}
				const meals = result.mealsLinked === 1 ? '1 meal' : `${result.mealsLinked} meals`;
				setOutcome(`${meals} on the calendar now use the meal idea ${name}.`);
				if(onChanged) {
					onChanged();
				}
				if(onConverted) {
					onConverted(result.mealIdeaId);
				}
			});
	};

	return (
		<section className='meal_idea_panel__section grocery_item_editor__as-idea'>
			<h6>Really a meal?</h6>
			{!!error && <p className='meal_idea_panel__name-error'>{error}</p>}
			{count > 0 &&
			<p className='grocery_item_editor__muted'>
				{`${count === 1 ? 'One meal' : `${count} meals`} on the calendar ${count === 1 ? 'is' : 'are'} called ${name} with no meal idea behind ${count === 1 ? 'it' : 'them'}.`}
			</p>
			}
			{!usesItem &&
			<label className='grocery_item_editor__as-idea-ingredient'>
				<input
					type='checkbox'
					className='filled-in'
					checked={withIngredient}
					onChange={(event) => setWithIngredient(event.target.checked)}
				/>
				<span>Keep {name} as its ingredient</span>
			</label>
			}
			<button type='button' className='btn-flat grocery_item_editor__as-idea-button' disabled={saving} {...onlineOnly(offline)} onClick={convert}>
				<MaterialIcon>lightbulb</MaterialIcon>
				{idea ? <>Link to the meal idea &ldquo;{idea.name}&rdquo;</> : 'Make it a meal idea'}
			</button>
		</section>
	);
}
